/**
 * /app/$tenantSlug/leave/comp-off — your Comp-Off balance + history
 * (migration 063).
 *
 * Shows the caller's comp-off credits (worked_date / expires_on / status /
 * remaining) and what they can still book: the credit left less the days
 * their pending comp-off requests already hold (QA 2026-10 leave #2, #9 —
 * a pending request reserves its credit). Consumption is NOT here — an
 * employee files a normal leave request against the Comp-Off (is_comp_off)
 * type. Granting, looking up and revoking a team member's credits is
 * Manage › Leave › Comp-Off (routes/…comp-off.tsx).
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarPlus } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { CompOffCreditList } from "@/components/leave/comp-off-team";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { fmtDays } from "@/lib/leave-format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCompOff, useCompOffBalance } from "@/hooks/use-leave";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/leave/comp-off",
)({
  component: CompOffRoute,
});

function CompOffRoute() {
  const tenantSlug = useTenantSlug();
  const credits = useCompOff();
  const balance = useCompOffBalance();

  const pending = balance.data?.pending_days ?? 0;

  return (
    <TimeAndLeaveShell
      title="Comp-Off"
      description="Compensatory off earned for working on a holiday or weekly-off. Use a Comp-Off leave request to consume credits."
    >
      <LeaveFeatureGate>
        <div className="space-y-6">
          <Card className="max-w-sm">
            <CardHeader>
              <CardTitle>Available Comp-Off</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {balance.isLoading ? (
                <Skeleton className="h-9 w-24" />
              ) : (
                <div>
                  <p className="text-3xl font-semibold">
                    {fmtDays(balance.data?.available_days ?? 0)}
                  </p>
                  {pending > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {fmtDays(balance.data?.credit_days ?? 0)} of credit,{" "}
                      {fmtDays(pending)} held by your pending comp-off
                      requests.
                    </p>
                  )}
                </div>
              )}
              {/* W2-27 — consumption flows through a normal leave request
                  against the Comp-Off type; surface a direct link to file
                  one. The /leave/new route accepts no initial-type search
                  param, so we link to it plainly (the user picks Comp-Off
                  in the form's leave-type select). */}
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/app/$tenantSlug/leave/new" params={{ tenantSlug }}>
                  <CalendarPlus className="h-4 w-4" />
                  Apply Comp-Off
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>My Comp-Off Credits</CardTitle>
            </CardHeader>
            <CardContent>
              {credits.data && credits.data.comp_off_credits.length > 0 ? (
                <CompOffCreditList credits={credits.data.comp_off_credits} />
              ) : (
                <p className="text-muted-foreground text-sm">
                  No comp-off credits yet.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
